<template>
  <div class="trend-card trend-card--notes">
    <div class="trend-card__header trend-card__header--tight">
      <div class="trend-card__title">
        <i class="fa-duotone fa-messages"></i>
        Recent Notes
      </div>

      <div class="notes-meta">
        <span class="notes-unread" v-if="unreadNotesCount > 0">
          <i class="fa-duotone fa-bell"></i>
          {{ unreadNotesCount }} unread
        </span>
        <span class="trend-card__meta">{{ safeRecentNotes.length }}</span>
      </div>
    </div>

    <div class="trend-card__body trend-card__body--notes">
      <div class="trend-loading notes-empty" v-if="notesLoading">
        Loading...
      </div>

      <div class="trend-empty notes-empty" v-else-if="!safeRecentNotes.length">
        No recent notes found
      </div>

      <div v-else class="notes-feed" ref="notesFeed">
        <div
          v-for="n in safeRecentNotes"
          :key="n.key"
          class="note-bubble"
          @click="openLoanFromNote(n)"
          :title="n.loanNumber ? `Open Loan ${n.loanNumber}` : 'Open Loan'"
        >
          <div class="note-bubble__top">
            <div class="note-bubble__left">
              <span class="note-loanline-inline">
                <i class="fa-duotone fa-file-invoice note-icon note-icon--loan"></i>
                <span class="note-loan-num">{{ n.loanNumber || "Loan" }}</span>

                <span class="note-inline" v-if="n.uwName" :title="uwTooltip(n)">
                  <span class="note-label">UW</span>
                  <span class="note-value">{{ n.uwName }}</span>
                </span>

                <span class="note-inline" v-if="n.loanOfficerName" :title="n.loanOfficerName">
                  <span class="note-label">LO</span>
                  <span class="note-value">{{ n.loanOfficerName }}</span>
                </span>

                <span
                  class="note-inline"
                  v-if="n.orgId !== null && n.orgId !== undefined && String(n.orgId).trim() !== ''"
                >
                  <span class="note-value">{{ n.orgId }}</span>
                </span>
              </span>

              <span class="note-unread-icon" v-if="n.isUnread" title="Unread">
                <i class="fa-duotone fa-bell note-icon note-icon--unread"></i>
              </span>
            </div>

            <div class="note-bubble__right">
              <span class="note-bubble__time">{{ timeAgo(n.created) }}</span>
            </div>
          </div>

          <div class="note-bubble__borrower" v-if="n.borrowerName">
            <span class="note-label">Borrower</span>
            <span class="note-value">{{ n.borrowerName }}</span>
          </div>

          <div class="note-bubble__message">
            <div class="note-bubble__message-icon">
              <i class="fa-duotone fa-comment-dots note-icon note-icon--chat"></i>
            </div>

            <div class="note-bubble__message-content">
              <div class="note-bubble__message-text">
                {{ n.notePlain }}
              </div>

              <div class="note-bubble__message-writer" v-if="n.writer">
                
                <span class="note-value ote-label">{{ n.writer }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ApiService from "@/api/api.service.js"
import signalRService from "@/api/signalr.service.js"

const LS_NOTES_SEEN = "initialuw_trends_notes_seen_v1"

export default {
  name: "InitialUwRecentNotesCard",
  props: {
    azureId: { type: String, required: true },
    authToken: { type: String, required: true },
    orgId: { type: [Number, String], default: null }
  },
  data() {
    return {
      notesLoading: false,
      recentNotesRaw: [],
      notesSeen: {},
      _notesRealtimeBound: false,
      _refreshNotesDebounce: null
    }
  },
  computed: {
    safeRecentNotes() {
      const list = Array.isArray(this.recentNotesRaw) ? this.recentNotesRaw : []
      return list.map(n => ({
        ...n,
        notePlain: this.htmlToText(n.note),
        isUnread: !this.notesSeen[n.key]
      }))
    },
    unreadNotesCount() {
      return this.safeRecentNotes.reduce((a, x) => a + (x.isUnread ? 1 : 0), 0)
    }
  },
  methods: {
    uwTooltip(n) {
      const parts = []
      if (n.uwTitle) parts.push(n.uwTitle)
      if (n.uwEmail) parts.push(n.uwEmail)
      return parts.join(" • ")
    },

    writerTooltip(n) {
      const parts = []
      if (n.writerJobTitle) parts.push(n.writerJobTitle)
      if (n.writerEmail) parts.push(n.writerEmail)
      return parts.join(" • ")
    },

    loadNotesSeen() {
      try {
        const raw = localStorage.getItem(LS_NOTES_SEEN)
        if (!raw) {
          this.notesSeen = {}
          return
        }
        const parsed = JSON.parse(raw)
        if (!parsed || typeof parsed !== "object") {
          this.notesSeen = {}
          return
        }
        this.notesSeen = parsed
      } catch {
        this.notesSeen = {}
      }
    },

    markNoteSeen(key) {
      if (!key) return
      if (this.notesSeen[key]) return
      const next = { ...(this.notesSeen || {}), [key]: true }
      this.notesSeen = next
      try {
        localStorage.setItem(LS_NOTES_SEEN, JSON.stringify(next))
      } catch {}
    },

    async fetchRecentNotes() {
      if (!this.azureId || !this.authToken) return
      this.notesLoading = true
      try {
        const resp = await ApiService.GetRecentLoanNotes(this.azureId, this.authToken, this.orgId, 20)
        const data = resp?.data?.data || resp?.data || []

        const seenLoanIds = new Set()
        const mapped = []

        for (const x of data) {
          const loanId = x.loanId
          if (!loanId) continue
          if (seenLoanIds.has(loanId)) continue
          seenLoanIds.add(loanId)

          const created = x.latestNoteCreated ? new Date(x.latestNoteCreated) : null
          const createdKey = created ? created.toISOString() : ""
          const messageType = String(x.latestNoteType || "").trim().toUpperCase() || "NOTE"

          const borrowerFirst = String(x.borrowerFirstName || "").trim()
          const borrowerLast = String(x.borrowerLastName || "").trim()
          const borrowerName = `${borrowerFirst} ${borrowerLast}`.trim()

          const key = `${loanId}_${createdKey}_${messageType}`

          mapped.push({
            key,
            loanId,
            loanNumber: x.loanNumber || "",
            borrowerName,
            note: x.latestNote || "",
            created: x.latestNoteCreated,
            messageType,
            uwName: x.uwName || x.noteWriterName || "",
            uwTitle: x.uwJobTitle || x.noteWriterJobTitle || "",
            uwEmail: x.uwEmail || x.noteWriterEmail || "",
            loanOfficerName: x.lo || "",
            orgId: x.orgid ?? this.orgId ?? null,
            writer: x.noteWriterName || "",
            writerJobTitle: x.noteWriterJobTitle || "",
            writerEmail: x.noteWriterEmail || "",
            writerAzureId: x.noteAzureId || ""
          })
        }

        this.recentNotesRaw = this.sortNotes(mapped).slice(0, 50)
      } finally {
        this.notesLoading = false
      }
    },

    normalizeNote(dto) {
      if (!dto) return null

      const loanId = dto.loanId || dto.LoanId
      if (!loanId) return null

      const created = dto.created || dto.Created || dto.latestNoteCreated || dto.LatestNoteCreated
      const createdDate = created ? new Date(created) : null
      const createdKey = createdDate && !isNaN(createdDate.getTime()) ? createdDate.toISOString() : ""

      const messageType = String(dto.messageType || dto.MessageType || dto.latestNoteType || dto.LatestNoteType || "NOTE")
        .trim()
        .toUpperCase()

      const key = `${loanId}_${createdKey}_${messageType}`

      const borrowerFirst = String(dto.borrowerFirstName || dto.BorrowerFirstName || "").trim()
      const borrowerLast = String(dto.borrowerLastName || dto.BorrowerLastName || "").trim()
      const borrowerName = `${borrowerFirst} ${borrowerLast}`.trim()

      return {
        key,
        loanId,
        loanNumber: dto.loanNumber || dto.LoanNumber || "",
        borrowerName,
        note: dto.note || dto.Note || dto.latestNote || dto.LatestNote || "",
        created,
        messageType,
        uwName:
          dto.uwName ||
          dto.UwName ||
          dto.noteWriterName ||
          dto.NoteWriterName ||
          dto.writer ||
          dto.Writer ||
          "",
        uwTitle:
          dto.uwTitle ||
          dto.UwTitle ||
          dto.uwJobTitle ||
          dto.UwJobTitle ||
          dto.noteWriterJobTitle ||
          dto.NoteWriterJobTitle ||
          dto.writerJobTitle ||
          dto.WriterJobTitle ||
          "",
        uwEmail:
          dto.uwEmail ||
          dto.UwEmail ||
          dto.noteWriterEmail ||
          dto.NoteWriterEmail ||
          dto.writerEmail ||
          dto.WriterEmail ||
          "",
        loanOfficerName: dto.lo || "",
        orgId: dto.orgid ?? dto.OrgId ?? null,
        writer: dto.writer || dto.Writer || dto.noteWriterName || dto.NoteWriterName || "",
        writerJobTitle: dto.writerJobTitle || dto.WriterJobTitle || dto.noteWriterJobTitle || dto.NoteWriterJobTitle || "",
        writerEmail: dto.writerEmail || dto.WriterEmail || dto.noteWriterEmail || dto.NoteWriterEmail || "",
        writerAzureId: dto.writerAzureId || dto.WriterAzureId || dto.noteAzureId || dto.NoteAzureId || ""
      }
    },

    sortNotes(list) {
      return (Array.isArray(list) ? list : []).slice().sort((a, b) => {
        const aa = a.created ? new Date(a.created).getTime() : 0
        const bb = b.created ? new Date(b.created).getTime() : 0
        return bb - aa
      })
    },

    upsertIntoNotes(note) {
      if (!note?.loanId || !note?.key) return

      const idx = this.recentNotesRaw.findIndex(x => x.key === note.key || x.loanId === note.loanId)

      if (idx >= 0) {
        const updated = { ...this.recentNotesRaw[idx], ...note }
        this.recentNotesRaw.splice(idx, 1)
        this.recentNotesRaw.unshift(updated)
      } else {
        this.recentNotesRaw.unshift(note)
      }

      this.recentNotesRaw = this.sortNotes(this.recentNotesRaw).slice(0, 50)
      this.scrollToTop()
    },

    refreshNotesSoon() {
      clearTimeout(this._refreshNotesDebounce)
      this._refreshNotesDebounce = setTimeout(async () => {
        await this.fetchRecentNotes()
        this.scrollToTop()
      }, 150)
    },

    openLoanFromNote(n) {
      if (!n?.loanId) return
      this.markNoteSeen(n.key)
      this.$emit("openLoan", n.loanId, null)
    },

    noteTypeIcon(type) {
      if (type === "UW") return "fa-user-shield"
      if (type === "LO") return "fa-user-tie"
      if (type === "PROC") return "fa-user-gear"
      return "fa-note"
    },

    htmlToText(html) {
      const s = String(html || "").trim()
      if (!s) return ""
      return s
        .replace(/<br\s*\/?>/gi, "\n")
        .replace(/<\/p>/gi, "\n")
        .replace(/<[^>]+>/g, "")
        .replace(/&nbsp;/g, " ")
        .replace(/&amp;/g, "&")
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/\n{3,}/g, "\n\n")
        .trim()
    },

    timeAgo(value) {
      const d = new Date(value)
      if (!d || isNaN(d.getTime())) return ""
      const diff = Date.now() - d.getTime()
      const mins = Math.floor(diff / 60000)
      if (mins < 1) return "just now"
      if (mins < 60) return `${mins}m ago`
      const hrs = Math.floor(mins / 60)
      if (hrs < 24) return `${hrs}h ago`
      const days = Math.floor(hrs / 24)
      return `${days}d ago`
    },

    scrollToTop() {
      const el = this.$refs.notesFeed
      if (!el) return
      el.scrollTop = 0
    },

    async subscribeRealtimeNotes() {
      if (this._notesRealtimeBound) return
      this._notesRealtimeBound = true

      await signalRService.startConditionsConnection()

      signalRService.onNoteSubmitted(message => {
        const dto = message?.data || message?.Data || message
        const note = this.normalizeNote(dto)
        if (!note) return

        if (this.orgId && dto.orgId && String(dto.orgId) !== String(this.orgId)) return

        this.upsertIntoNotes(note)

        const isPartial = !note.loanNumber && !note.borrowerName && !note.note && !note.created

        if (isPartial) {
          this.refreshNotesSoon()
        }
      })
    }
  },

  async mounted() {
    this.loadNotesSeen()
    await this.fetchRecentNotes()
    await this.subscribeRealtimeNotes()
  },

  beforeUnmount() {
    this._notesRealtimeBound = false
    clearTimeout(this._refreshNotesDebounce)
  },

  watch: {
    azureId() {
      this.fetchRecentNotes()
    },
    authToken() {
      this.fetchRecentNotes()
    },
    orgId() {
      this.fetchRecentNotes()
    }
  }
}
</script>

<style scoped>
.trend-card {
  background: var(--background);
  border: 1px solid var(--border-ltr);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 0 rgba(0, 0, 0, 0.02);
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.trend-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  border-bottom: 1px solid var(--border-ltr);
  background: rgba(5, 29, 58, 0.02);
}

.trend-card__header--tight {
  padding: 9px 10px;
}

.trend-card__title {
  font-size: 13px;
  font-weight: 950;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: var(--text-primary-on-background);
}

.trend-card__meta {
  font-size: 12px;
  font-weight: 800;
  color: var(--text-secondary-on-background);
}

.trend-card--notes .trend-card__title i.fa-duotone.fa-messages {
  --fa-primary-color: rgba(40, 110, 255, 0.95);
  --fa-secondary-color: rgba(40, 110, 255, 0.26);
}

.trend-card__body--notes {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 10px 10px 12px;
}

.trend-loading,
.trend-empty {
  height: 210px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 900;
  color: var(--text-secondary-on-background);
  gap: 10px;
}

.notes-meta {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.notes-unread {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 900;
  border: 1px solid rgba(255, 65, 65, 0.25);
  background: rgba(255, 65, 65, 0.08);
}

.notes-unread i.fa-duotone.fa-bell {
  --fa-primary-color: rgba(255, 65, 65, 0.95);
  --fa-secondary-color: rgba(255, 65, 65, 0.28);
}

.notes-feed {
  height: 100%;
  overflow: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-right: 6px;
}

.note-bubble {
  border: 1px solid var(--border-ltr);
  border-radius: 12px;
  padding: 10px 10px;
  background: rgba(5, 29, 58, 0.02);
  display: flex;
  flex-direction: column;
  gap: 10px;
  cursor: pointer;
  transition: transform 120ms ease, box-shadow 120ms ease, border-color 120ms ease;
}

.note-bubble:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 26px rgba(0, 0, 0, 0.06);
  border-color: rgba(40, 110, 255, 0.22);
}

.note-bubble__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.note-bubble__left {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  flex-wrap: wrap;
}

.note-loanline-inline {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-size: 11px;
  color: var(--text-primary-on-background);
  white-space: nowrap;
}

.note-loan-num {
  font-weight: 950;
}

.note-inline {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  min-width: 0;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
}
.note-bubble__message-writer::before {
  content: "—";
  opacity: 0.55;
}

.note-label {
  font-size: 10.5px;
  font-weight: 800;
  color: var(--text-secondary-on-background);
  opacity: 0.75;
}

.note-value {
  font-size: 11px;
  font-weight: 900;
  color: var(--text-primary-on-background);
  opacity: 0.92;
  overflow: hidden;
  text-overflow: ellipsis;
}

.note-bubble__time {
  font-size: 10.5px;
  font-weight: 800;
  color: var(--text-secondary-on-background);
  opacity: 0.75;
}

.note-unread-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 999px;
  border: 1px solid rgba(255, 65, 65, 0.35);
  background: rgba(255, 65, 65, 0.10);
  box-shadow: 0 6px 14px rgba(255, 65, 65, 0.12);
}

.note-unread-icon i.fa-duotone.fa-bell {
  --fa-primary-color: rgba(255, 65, 65, 0.95);
  --fa-secondary-color: rgba(255, 65, 65, 0.28);
}

.note-bubble__borrower {
  display: inline-flex;
  align-items: baseline;
  gap: 8px;
  padding: 2px 2px;
}

.note-bubble__message {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 10px;
  border-radius: 12px;
  border: 1px solid rgba(40, 110, 255, 0.16);
  background: rgba(40, 110, 255, 0.06);
}

.note-bubble__message-icon {
  width: 28px;
  height: 28px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(40, 110, 255, 0.18);
  background: rgba(40, 110, 255, 0.10);
  flex: 0 0 auto;
}

.note-bubble__message-icon i.fa-duotone.fa-comment-dots {
  --fa-primary-color: rgba(40, 110, 255, 0.95);
  --fa-secondary-color: rgba(40, 110, 255, 0.25);
}

.note-bubble__message-content {
  min-width: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.note-bubble__message-text {
  font-size: 12.5px;
  font-weight: 850;
  color: var(--text-primary-on-background);
  line-height: 1.4;
  white-space: pre-wrap;
  opacity: 0.92;
}

.note-bubble__message-writer {
  display: inline-flex;
  align-items: baseline;
  gap: 8px;
}

.note-icon--loan {
  --fa-primary-color: rgba(40, 110, 255, 0.95);
  --fa-secondary-color: rgba(40, 110, 255, 0.22);
}

.note-icon--chat {
  --fa-primary-color: rgba(40, 110, 255, 0.95);
  --fa-secondary-color: rgba(40, 110, 255, 0.25);
}

.note-icon--unread {
  --fa-primary-color: rgba(255, 65, 65, 0.95);
  --fa-secondary-color: rgba(255, 65, 65, 0.28);
}
</style>
